import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft, FaPencilAlt } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, Typography } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_rubricMutation,
  useGET_rubricByIdQuery,
  usePOST_rubricMutation,
  usePATCH_rubricMutation,
} from 'redux/api/rubricApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'

import useCrudData from 'utils/customHooks/useCrudData'
import { GREY_BORDER } from 'utils/customizedTheme'
import formModel, { initialValues, validationSchema } from 'utils/formModels/rubricModel'
import { crudColorMap, crudIconMap, gradeSelectData, rubricLevelsSelectData, rubricPointsSelectData, translateGradeLevel } from 'utils/helpers'

import { ActionType, IRubric } from 'types'
import Rubric from 'components/molecules/Rubric'
import DialogPopup from 'components/molecules/DialogPopup'
import SelectField from 'components/atoms/fields/SelectField'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import RubricCriterionList from 'components/utilized/rubrics/RubricCriterionList'
import _, { fromPairs } from 'lodash'
import { useGET_domainsSelectQuery } from 'redux/api/goalbankApi'
import { useGET_subjectsSelectQuery } from 'redux/api/subjectsApi'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const RubricForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [popupRubric, setPopupRubric] = useState(null)

  const backToSlug = '/rubrics'
  const editSlug = `/rubrics/edit/${id}`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.rubrics')}`,
    entityId: id,
    getEntityQuery: useGET_rubricByIdQuery,
    createEntityMutation: usePOST_rubricMutation,
    editEntityMutation: usePATCH_rubricMutation,
    deleteEntityMutation: useDELETE_rubricMutation,
  })

  const { title, subject, domain, grade_level, levels, points_total, is_peer_assessment_only, rubric_criterions } = formModel()

  const sumPoints = (criterions) => {
    let sum = 0
    criterions.forEach(rc => {
      sum += _.orderBy(rc.rubric_sections, ['points'], ['desc'])[0].points
    })
    return sum
  }
  
  const formValues = entity ? {
    ...initialValues,
    ...entity,
    grade_level: entity.grade_level >= 0  ? {
      id: entity.grade_level,
      title: translateGradeLevel(entity.grade_level, true)
    } : null,
    levels: entity.levels ? {
      id: entity.levels,
      title: entity.levels.toString()
    } : null,
    points_total: entity.points_total ? {
      id: entity.points_total,
      title: entity.points_total.toString()
    } : null,
  } : {
    ...initialValues
  }



  if(id && !entity) return <></>
  
  if(entity && viewOnly) return <>
        <Rubric id={entity?.id} />

        <div style = {{ marginTop: '20px', display: 'flex' }}>
          <Button
            variant='outlined'
            color='black'
            onClick={() => navigate(backToSlug)}
          >
              <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
              {`${t('common.back')}`}
          </Button>

          {entity.actions?.is_editable && <Button
            variant='outlined'
            color='black'
            onClick={() => navigate(`/rubrics/edit/${id}`)}
            sx={{ marginLeft: '10px' }}
          >
              <FaPencilAlt style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
              {`${t('actions.edit')}`}
          </Button>}


          {action === 'delete' && entity.actions?.is_deletable && <SubmitButton
              color={crudColorMap[action]}
              text={`${t('form-actions.' + action)}`}
              icon={crudIconMap[action]}
              isLoading={isLoading}
              onClick={() => {
                handleSubmit(action, id, {})
              }}
            />
          }      
        </div>
    </>

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...formValues,    
      }}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
  
        const data: IRubric = {
          ...values,
          grade_level: values.grade_level?.id ?? "",
          subject_id: values.subject?.id ?? "",
          domain_id: values.domain?.id ?? "",
          levels: values.levels?.id ?? "",
          points_total: values.points_total?.id ?? "",
          rubric_criterions: values.rubric_criterions.map(rc => {
            return {
              ...rc,
              benchmark_id: rc.benchmark?.id ?? null 
            }
          })
        }

        handleSubmit(action, id, data)
      }}
    >
      {({ values, setFieldValue, errors }) => {


      return (
      <Form> 
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>


          <Grid item xs={12} md={2}>
            <SelectField
                 fieldProps={{ ...grade_level }}
                  apiCall={gradeSelectData}
                  disabled={viewOnly}
              />
          </Grid>
          <Grid item xs={12} md={4}>
              <SelectField
                fieldProps={{ ...subject }}
                apiCall={useGET_subjectsSelectQuery}
                disabled={viewOnly}
              />
          </Grid>
          <Grid item xs={12} md={4}>
              <SelectField
                filters={{
                  grade_level: values.grade_level?.id ?? 0,
                  subject: values.subject?.id ?? undefined
                }}
                fieldProps={{ ...domain }}
                apiCall={useGET_domainsSelectQuery}
                disabled={viewOnly}
              />
          </Grid>

          <Grid item xs={12} md={1}>
            <Button
                variant='outlined'
                size='small'
                color='black'
                onClick={() => {
                    setPopupRubric(12)
                }}
              >
                {`${t('rubrics.view-example-rubric')}`}
              </Button>
          </Grid>


          <Grid item xs={12} md={4}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>


          <Grid item xs={12} md={2}>
            <SelectField
                 fieldProps={{ ...levels }}
                  apiCall={rubricLevelsSelectData}
                  disabled={viewOnly}
              />        
          </Grid>

          <Grid item xs={12} md={2}>
            <SelectField
                 fieldProps={{ ...points_total }}
                  apiCall={rubricPointsSelectData}
                  disabled={viewOnly}
              />        
          </Grid>

          <Grid item xs={12} md={4}>
            <CheckboxField
                  fieldProps={{ ...is_peer_assessment_only }}
                  disabled={viewOnly}
                />     
          </Grid>    
          <Grid item xs={12} sx={{ borderBottom: `3px solid ${GREY_BORDER}` }}>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: '700', fontFamily: 'Newsreader' }}>
              {`${t('rubrics.criterions')}`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <RubricCriterionList 
              fieldProps={{ ...rubric_criterions }} 
              disabled={viewOnly} 
              cells={values.levels?.id ?? 3} 
            />
          </Grid>
          

          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>

              {action === 'edit' && entity?.actions?.is_editable && 
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(`/rubrics/view/${id}`)}
                  sx={{ marginLeft: '10px'  }}
                >
                  {`${t('common.preview')}`}
                </Button>  
              }
              
              {action !== 'view' && 
                    <div style={{ 
                          marginLeft: "auto", 
                          padding: "0px 10px",
                          color: values.points_total?.id == sumPoints(values.rubric_criterions) ? 'green' : 'red' }}>
                      {t('rubrics.points-total')}: <b>{sumPoints(values.rubric_criterions)}&nbsp;/&nbsp;{values.points_total?.id}</b>
                    </div>
              }

              {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    isLoading={isLoading}
                    disabled={!values.points_total?.id || values.points_total?.id != sumPoints(values.rubric_criterions)}
                  />
              )}

            </Box>
          </Grid>
          
        
                <DialogPopup
                  title={t('rubrics.view-example-rubric')}
                  open={!!popupRubric}
                  onClose={() => setPopupRubric(null)}
                  popupWidth={'800px'}
                >
                  
                  <img src='https://www.esybee.com/img/rubric.jpg' style={{ maxWidth:'100%' }}></img>
                </DialogPopup>
                
        </Grid>
      </Form>
      

          )
        }}
    </Formik>
  )
}
export default RubricForm
